import { Trans } from '@lingui/react/macro';

import React from 'react';
import { postMeGrant, postAgentRecover } from '../lib/api';
import { useUIActionStore } from '../stores';

export interface CapabilityGrantAction {
  action: 'grant' | 'reject' | 'snooze';
  agent_id: string;
  capability: string;
  scope: string;
  request_id: string;
}

/**
 * Server reads last error reason from agent_state_log; client only sends
 * agent_id + request_id.
 */
export interface AgentRecoveryAction {
  action: 'recover';
  agent_id: string;
  reason: string;
  request_id: string;
}

export interface SystemMessageBubbleProps {
  /** Pre-rendered HTML body (e.g. markdown rendered upstream). */
  bodyHTML: string;
  /** Parsed capability request; null omits the grant controls. */
  grantAction?: CapabilityGrantAction | null;
  /** Parsed recovery request; null omits the recovery control. */
  recoveryAction?: AgentRecoveryAction | null;
  /** Parsed application action, dispatched by its semantic action kind. */
  fallback?: { kind?: string; label?: string; action?: string } | null;
  /** Test seam — defaults to real postMeGrant API call. */
  onGrant?: (payload: CapabilityGrantAction) => Promise<void>;
  /** Test seam — defaults to real postAgentRecover API call. */
  onRecover?: (payload: AgentRecoveryAction) => Promise<void>;
}

export function isCapabilityGrantAction(qa: unknown): qa is CapabilityGrantAction {
  if (!qa || typeof qa !== 'object') return false;
  const o = qa as Record<string, unknown>;
  return (o.action === 'grant' || o.action === 'reject' || o.action === 'snooze')
    && typeof o.agent_id === 'string' && o.agent_id.length > 0
    && typeof o.capability === 'string' && o.capability.length > 0
    && typeof o.scope === 'string' && o.scope.length > 0
    && typeof o.request_id === 'string' && o.request_id.length > 0;
}

export function isAgentRecoveryAction(qa: unknown): qa is AgentRecoveryAction {
  if (!qa || typeof qa !== 'object') return false;
  const o = qa as Record<string, unknown>;
  return o.action === 'recover'
    && typeof o.agent_id === 'string' && o.agent_id.length > 0
    && typeof o.reason === 'string' && o.reason.length > 0
    && typeof o.request_id === 'string' && o.request_id.length > 0;
}

const SystemMessageBubble: React.FC<SystemMessageBubbleProps> = ({ bodyHTML, grantAction, recoveryAction, fallback, onGrant, onRecover }) => {
  const handleClick = (action: 'grant' | 'reject' | 'snooze') => async () => {
    if (!grantAction) return;
    const payload: CapabilityGrantAction = { ...grantAction, action };
    if (onGrant) {
      await onGrant(payload);
    } else {
      await postMeGrant(payload).catch(() => { /* swallow; caller would show toast */ });
    }
  };
  const handleRecover = async () => {
    if (!recoveryAction) return;
    if (onRecover) {
      await onRecover(recoveryAction);
    } else {
      await postAgentRecover(recoveryAction).catch(() => { /* swallow; caller would show toast */ });
    }
  };
  return (
    <div className="message-item message-system">
      <div className="message-system-content">
        <div className="message-text" dangerouslySetInnerHTML={{ __html: bodyHTML }} />
        {grantAction && (
          <div className="message-system-permission-grant" data-permission-grant="true">
            <button
              type="button"
              className="message-system-quick-action"
              data-permission-grant-action="primary"
              data-action="grant"
              onClick={handleClick('grant')}
            ><Trans>Authorize</Trans></button>
            <button
              type="button"
              className="message-system-quick-action"
              data-permission-grant-action="danger"
              data-action="reject"
              onClick={handleClick('reject')}
            ><Trans>Reject</Trans></button>
            <button
              type="button"
              className="message-system-quick-action"
              data-permission-grant-action="ghost"
              data-action="snooze"
              onClick={handleClick('snooze')}
            ><Trans>Later</Trans></button>
          </div>
        )}
        {recoveryAction && !grantAction && (
          <div className="message-system-agent-recovery" data-agent-recovery="true">
            <button
              type="button"
              className="message-system-quick-action"
              data-agent-recovery-action="recover"
              data-action="recover"
              onClick={handleRecover}
            ><Trans>Reconnect</Trans></button>
          </div>
        )}
        {!grantAction && !recoveryAction && fallback && fallback.kind === 'button' && fallback.action === 'open_agent_manager' && (
          <button
            type="button"
            className="message-system-quick-action"
            data-action={fallback.action}
            onClick={() => {
              useUIActionStore.getState().dispatch({ type: fallback.action! });
            }}
          ><Trans>Add agent</Trans></button>
        )}
      </div>
    </div>
  );
};

export default SystemMessageBubble;
